// PWA: registrazione service worker + stato "installabile".
//
// Android/Chrome espone l'evento `beforeinstallprompt` e permette di aprire il
// dialogo nativo. iOS/Safari no: lì l'unica via è Condividi → "Aggiungi a Home",
// quindi mostriamo istruzioni invece del bottone.

export type Platform = "ios" | "android" | "desktop";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function notify() {
  for (const l of listeners) l();
}

/** Registra il service worker (solo in produzione: in dev darebbe cache fantasma). */
export function registerServiceWorker() {
  if (!("serviceWorker" in navigator) || import.meta.env.DEV) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch((err) => {
      console.warn("[pwa] registrazione service worker fallita", err);
    });
  });
}

/** Intercetta l'evento di installabilità di Chrome/Android. */
export function watchInstallPrompt() {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
  });
}

export function subscribeInstallState(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function canPromptInstall(): boolean {
  return deferredPrompt !== null;
}

/** Apre il dialogo nativo di installazione (solo dove disponibile). */
export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (!deferredPrompt) return "unavailable";
  await deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  deferredPrompt = null;
  notify();
  return outcome;
}

/**
 * Marca l'elemento radice con piattaforma e modalità: il CSS può adattarsi
 * senza che i componenti sappiano nulla di user agent.
 * `data-platform="ios|android|desktop"`, `data-standalone="true"`.
 */
export function applyPlatformFlags() {
  const root = document.documentElement;
  root.dataset.platform = detectPlatform();
  if (isStandalone()) root.dataset.standalone = "true";
  else delete root.dataset.standalone;
}

export function detectPlatform(): Platform {
  const ua = navigator.userAgent;
  // iPadOS 13+ si spaccia per Mac: lo si riconosce dal touch
  const isIpadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/.test(ua) || isIpadOS) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
}

/** True quando l'app gira già installata (standalone), non nel browser. */
export function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS usa una proprietà non standard su navigator
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}
